import { api, getToken, setToken, clearToken } from "./api.js";

let currentUser = null;
let initializing = true;
const listeners = new Set();

function notify() {
  listeners.forEach((fn) => fn());
}

export function onAuthChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getCurrentUser() {
  return currentUser;
}

export function isAuthenticated() {
  return !!getToken();
}

export function isInitializing() {
  return initializing;
}

export async function restoreSession() {
  const token = getToken();
  if (token) {
    try {
      currentUser = await api.get("/auth/me");
    } catch (_) {
      clearToken();
      currentUser = null;
    }
  }
  initializing = false;
  notify();
}

export async function login(email, password) {
  const data = await api.post("/auth/login", { email, password });
  setToken(data.token);
  currentUser = data.user;
  notify();
}

/** website: the form's hidden field (empty unless a bot filled it in); formMs: how long
 *  the form was open. The API uses both against sign-up bots (services/accounts.py). */
export async function register({ email, username, password, displayName, certificationLevel, website = "", formMs }) {
  const data = await api.post("/auth/register", {
    email,
    username,
    password,
    display_name: displayName || null,
    certification_level: certificationLevel || null,
    website,
    form_ms: formMs ?? null,
  });
  setToken(data.token);
  currentUser = data.user;
  notify();
}

export function logout() {
  clearToken();
  currentUser = null;
  notify();
}

/** Updates the current user's display name and refreshes the cached
 * profile (so anything reading getCurrentUser() — e.g. the nav bar —
 * picks up the change immediately without a manual refetch). */
/** Mirrors User.MAX_BIO_LENGTH in app/models.py — the API rejects longer. */
export const MAX_BIO_LENGTH = 500;

/** Patches whichever profile fields are given and refreshes the session.
 *
 * Absent keys are left alone by the API, so editing one field never
 * clears another. */
export async function updateProfile(fields) {
  const data = await api.patch("/auth/me", fields);
  currentUser = data;
  notify();
}

export async function updateDisplayName(displayName) {
  return updateProfile({ display_name: displayName });
}

export async function updateCertificationLevel(level) {
  // Sent trimmed; an empty value clears it, which the API treats as
  // "not set" rather than as an error.
  return updateProfile({ certification_level: (level ?? "").trim() });
}

/** "metric" or "imperial": how depths, temperatures and pressures are
 * shown and entered (js/units.js). */
export async function updateUnits(units) {
  return updateProfile({ units });
}

export async function updateBio(bio) {
  // Sent as-is apart from the ends: newlines and indentation are part of
  // what the diver wrote, and the profile renders it with pre-wrap.
  return updateProfile({ bio: bio.trim() });
}

/** Throws an ApiError with a clear message (e.g. "current password is
 * incorrect") if the change is rejected — callers should surface that
 * message directly rather than a generic failure. */
export async function changePassword(currentPassword, newPassword) {
  await api.post("/auth/change-password", {
    current_password: currentPassword,
    new_password: newPassword,
  });
}

/** Deletes the signed-in account and everything it created (the API checks the
 *  password), then signs out here. */
export async function deleteAccount(password) {
  await api.delete("/auth/me", { password });
  logout();
}

/** Confirms an email address from a token out of the verification link.
 * Deliberately usable while signed out — the signed token is the
 * credential, so the link works in whatever browser the mail client
 * opens. When there *is* a session, the cached user is refreshed from
 * the response so the unverified banner clears without a reload. */
export async function verifyEmail(token) {
  const data = await api.post("/auth/verify", { token });
  if (getToken() && data.user && currentUser && currentUser.id === data.user.id) {
    currentUser = data.user;
    notify();
  }
  return data.user;
}

/** Asks the API to send another confirmation email. Requires a session. */
export async function resendVerification() {
  return api.post("/auth/resend-verification", {});
}

/** True when we know the signed-in user still needs to confirm. Absent
 * `email_verified` (older API, or no session) counts as "nothing to
 * nag about" rather than showing a spurious banner. */
export function needsEmailVerification() {
  return currentUser != null && currentUser.email_verified === false;
}

/** Starts a password reset. Resolves the same way whether or not the
 * address has an account — the API deliberately doesn't say, so neither
 * can the UI. */
export async function requestPasswordReset(email) {
  return api.post("/auth/forgot-password", { email });
}

/** Completes a reset with the token from the email. Signs the user in on
 * success, since they've just proved control of the inbox and proved the
 * new password — making them type it again immediately would be
 * ceremony, not security. */
export async function resetPassword(token, newPassword) {
  const data = await api.post("/auth/reset-password", {
    token,
    new_password: newPassword,
  });
  return data.user;
}

/** Whether the signed-in user is a site-wide moderator. The flag only
 * arrives on your own account (include_private), so this is never true
 * for someone looking at another diver's profile. */
export function isSiteAdmin() {
  return currentUser != null && currentUser.is_site_admin === true;
}
